const {
    isAllLocationsGroupKey,
    resolveReviewsLinkAction
} = require('../../../blocks/reviews-summary-block/link-action-rules')

const formatShortCount = (value) => {
    const v = Number(value || 0)
    const tidy = (n) => {
        const fixed = Number(n).toFixed(1)
        return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed
    }
    // Lowercase to match existing short formatting (e.g. 18.3k).
    if (v >= 1000000) return `${tidy(v / 1000000)}m`
    if (v >= 1000) return `${tidy(v / 1000)}k`
    return v.toLocaleString()
}

const formatFullCount = (value) => {
    const v = Number(value || 0)
    return Number.isFinite(v) ? v.toLocaleString() : '0'
}

const formatCount = (value, format) => {
    const mode = (format || '').toLowerCase()
    if (mode === 'short') return formatShortCount(value)
    if (mode === 'auto') {
        const v = Number(value || 0)
        return v >= 1000 ? formatShortCount(value) : formatFullCount(value)
    }
    return formatFullCount(value)
}

const toBool = (value, defaultValue) => {
    if (value === undefined || value === null) return defaultValue
    if (typeof value === 'boolean') return value
    const normalised = String(value).toLowerCase()
    if (['true', '1', 'yes', 'on'].includes(normalised)) return true
    if (['false', '0', 'no', 'off'].includes(normalised)) return false
    return defaultValue
}

const escapeAttr = (value) => {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

const escapeHtml = (value) => {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

const jsonEscape = (value) => {
    return JSON.stringify(value ?? '').slice(1, -1)
}

const GOOGLE_REVIEWS_SETUP_MESSAGE = "Google Reviews isn't fully set up yet. Add a Places API key in Settings > Apps & Integrations > Google Reviews, then add a Google Place ID in Settings > Business > Locations."

module.exports = function (provider, groupToken, options) {
    const root = options?.data?.root || {}
    const targetToken = String(groupToken || '').trim()
    const isSourceToken = /^source:/i.test(targetToken)
    const scope = (options?.hash?.scope || 'website').toString()
    const scopedToken = !isSourceToken && scope && scope !== 'website'
        ? `${targetToken}|scope:${scope}`
        : targetToken

    const builderSetupState = String(
        root?.__reviewsSetup?.google
        || root?.reviewsSetup?.google
        || ''
    ).trim().toLowerCase()
    const isBuilderPreview = options?.data?.root?.__builder === true
    const showSetupWarning = isBuilderPreview && builderSetupState === 'not-configured'
    if (showSetupWarning) {
        const html = `<span style='display:inline-flex;border:1px solid #fcd34d;background:#fffbeb;color:#92400e;border-radius:6px;padding:6px 8px;'>${GOOGLE_REVIEWS_SETUP_MESSAGE}</span>`
        return jsonEscape(html)
    }

    const placeholder = isBuilderPreview || options?.hash?.placeholder === true

    let summaryValue = root?.reviews?.[provider]?.[scopedToken]
    let ratingValue = summaryValue?.avg
    let countValue = summaryValue?.count
    let mapsUrl = summaryValue?.mapsUrl

    if (ratingValue === undefined && countValue === undefined && mapsUrl === undefined) {
        summaryValue = root?.reviews?.[provider]?.[targetToken]
        ratingValue = summaryValue?.avg
        countValue = summaryValue?.count
        mapsUrl = summaryValue?.mapsUrl
    }

    if (ratingValue === undefined && countValue === undefined && !placeholder) {
        return ''
    }

    if (ratingValue === undefined && countValue === undefined && placeholder) {
        const html = `<span style='color:#999;'>Loading reviews...</span>`
        return jsonEscape(html)
    }

    const precision = Number(options?.hash?.precision ?? 1)
    const ratingNumber = Number(ratingValue || 0)
    const ratingText = Number.isFinite(ratingNumber) ? ratingNumber.toFixed(precision) : ''

    const countNumber = Number(countValue || 0)
    const shortCount = toBool(options?.hash?.shortCount ?? options?.hash?.short, false)
    const countFormat = (options?.hash?.countFormat || options?.hash?.format || '').toString().toLowerCase()
    const resolvedFormat = countFormat || (shortCount ? 'short' : 'full')
    const countText = formatCount(countNumber, resolvedFormat)

    let variant = String(options?.hash?.variant || options?.hash?.style || 'inline').toLowerCase()
    if (variant === 'compact') variant = 'inline'
    if (!['inline', 'pill', 'outline'].includes(variant)) variant = 'inline'
    const showLogo = toBool(options?.hash?.logo ?? options?.hash?.showLogo, false)
    const showCount = (() => {
        const show = toBool(options?.hash?.showCount, true)
        const hide = toBool(options?.hash?.hideCount, false)
        return hide ? false : show
    })()
    const showRating = toBool(options?.hash?.showRating, true)
    const showStars = toBool(options?.hash?.showStars, true)
    const parenCount = toBool(options?.hash?.parenCount, true)
    const customHref = (() => {
        const href = (options?.hash?.customUrl || options?.hash?.url || options?.hash?.href || '').toString().trim()
        if (!href) return ''
        if (/^javascript:/i.test(href)) return ''
        return href
    })()
    const groupKey = String(targetToken || '')
        .split('|')[0]
        .split(':')
        .pop()
        .trim()
    const isAllLocationsGroup = !isSourceToken && isAllLocationsGroupKey(groupKey)
    const sourceCountFromSummary = isSourceToken
        ? 1
        : Number.isSafeInteger(summaryValue?.sourceCount) && summaryValue.sourceCount >= 0
            ? summaryValue.sourceCount
            : summaryValue?.sources && typeof summaryValue.sources === 'object'
                ? Object.keys(summaryValue.sources).length
                : null
    const sourceCountRaw = isSourceToken
        ? 1
        : Number(options?.hash?.sourceCount ?? options?.hash?.sources ?? sourceCountFromSummary)
    const sourceCount = isSourceToken ? 1 : (Number.isFinite(sourceCountRaw) ? sourceCountRaw : null)
    const actionState = resolveReviewsLinkAction({
        configuredLinkTarget: options?.hash?.linkTarget || options?.hash?.linkMode,
        legacyLinkEnabled: toBool(options?.hash?.link, true),
        selectionCount: sourceCount,
        isAllLocationsGroup
    })
    const linkTarget = actionState.nextLinkTarget

    let wrapperStyle = 'display:inline-block;max-width:100%;font:inherit;color:inherit;line-height:1;text-align:inherit;'
    if (variant === 'pill') {
        wrapperStyle += 'padding:2px 8px;border-radius:999px;background:var(--reviews-chip-pill-bg,rgba(255,255,255,0.82));border:1px solid var(--reviews-chip-pill-border,rgba(0,0,0,0.24));'
    } else if (variant === 'outline') {
        wrapperStyle += 'padding:2px 8px;border-radius:var(--button-roundness,var(--container-roundness,6px));background:transparent;border:1px solid var(--reviews-chip-pill-border,rgba(0,0,0,0.24));'
    }

    const suffixStyle = (options?.hash?.copy || options?.hash?.suffix || 'on-google').toString().toLowerCase()
    const customSuffixText = String(options?.hash?.copyText || options?.hash?.suffixText || '').trim()
    let suffixText = 'on Google'
    if (suffixStyle === 'google-reviews' || suffixStyle === 'reviews') suffixText = 'Google reviews'
    if (suffixStyle === 'custom') suffixText = customSuffixText
    if (suffixStyle === 'none') suffixText = ''

    const fillPercent = Math.max(0, Math.min(100, (ratingNumber / 5) * 100))
    const clipPath = 'polygon(8.82% 0.00%,6.34% 34.92%,0.00% 38.07%,4.82% 62.98%,3.37% 100.00%,8.82% 80.35%,14.28% 100.00%,12.82% 62.98%,17.65% 38.07%,11.31% 34.92%,8.82% 0.00%,29.41% 0.00%,26.93% 34.92%,20.59% 38.07%,25.41% 62.98%,23.96% 100.00%,29.41% 80.35%,34.87% 100.00%,33.41% 62.98%,38.24% 38.07%,31.90% 34.92%,29.41% 0.00%,50.00% 0.00%,47.51% 34.92%,41.18% 38.07%,46.00% 62.98%,44.54% 100.00%,50.00% 80.35%,55.46% 100.00%,54.00% 62.98%,58.82% 38.07%,52.49% 34.92%,50.00% 0.00%,70.59% 0.00%,68.10% 34.92%,61.76% 38.07%,66.59% 62.98%,65.13% 100.00%,70.59% 80.35%,76.04% 100.00%,74.59% 62.98%,79.41% 38.07%,73.07% 34.92%,70.59% 0.00%,91.18% 0.00%,88.69% 34.92%,82.35% 38.07%,87.18% 62.98%,85.72% 100.00%,91.18% 80.35%,96.63% 100.00%,95.18% 62.98%,100.00% 38.07%,93.66% 34.92%,91.18% 0.00%)'
    const logoHtml = showLogo
        ? `<svg aria-hidden='true' focusable='false' class='google-icon' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' style='display:inline-block;width:var(--reviews-chip-logo-size,1.08em);height:var(--reviews-chip-logo-size,1.08em);flex:0 0 auto;line-height:0;'>
                <g fill='none'>
                    <path fill='#2A84FC' d='M21.579 12.234c0-.677-.055-1.359-.172-2.025h-9.403v3.839h5.384a4.614 4.614 0 0 1-1.992 3.029v2.49h3.212c1.887-1.735 2.97-4.3 2.97-7.333Z'></path>
                    <path fill='#00AC47' d='M12.004 21.974c2.688 0 4.956-.882 6.608-2.406l-3.213-2.491c-.894.608-2.047.952-3.392.952-2.6 0-4.806-1.754-5.597-4.113H3.095v2.568a9.97 9.97 0 0 0 8.909 5.49Z'></path>
                    <path fill='#FFBA00' d='M6.407 13.916a5.971 5.971 0 0 1 0-3.817V7.53H3.095a9.977 9.977 0 0 0 0 8.953l3.312-2.568Z'></path>
                    <path fill='#FC2C25' d='M12.004 5.982a5.417 5.417 0 0 1 3.824 1.494l2.846-2.846a9.581 9.581 0 0 0-6.67-2.593A9.967 9.967 0 0 0 3.095 7.53l3.312 2.57c.787-2.363 2.996-4.117 5.597-4.117Z'></path>
                </g>
            </svg>`
        : ''
    const starsHtml = showStars
        ? `<span aria-hidden='true' style='display:inline-block;flex:0 0 auto;width:5.05em;min-width:5.05em;max-width:5.05em;height:0.85em;line-height:0;transform:translateY(-0.03em);clip-path:${clipPath};background:linear-gradient(90deg,var(--reviews-chip-star-fill,#f4b400) ${fillPercent}%,var(--reviews-chip-star-empty,#d1d5db) ${fillPercent}%);filter:var(--reviews-chip-star-filter,none);'></span>`
        : ''

    const ratingHtml = showRating
        ? `<span style='font-variant-numeric:tabular-nums;font-weight:600;flex:0 0 auto;'>${ratingText}</span>`
        : ''

    const countDisplay = showCount ? (parenCount ? `(${countText})` : countText) : ''
    const secondaryLabel = [countDisplay, suffixText].filter(Boolean).join(' ')
    const safeSecondaryLabel = escapeHtml(secondaryLabel)
    const countHtml = secondaryLabel
        ? `<span style='display:inline-flex;align-items:center;opacity:var(--reviews-chip-count-opacity,0.82);font-size:0.9em;line-height:1;white-space:normal;overflow-wrap:anywhere;min-width:0;'>${safeSecondaryLabel}</span>`
        : ''

    const hasPrimaryCluster = [logoHtml, ratingHtml, starsHtml].filter(Boolean).length > 0
    const primaryClusterHtml = hasPrimaryCluster
        ? `<span style='display:inline-flex;align-items:center;column-gap:0.35em;white-space:nowrap;line-height:1;flex:0 0 auto;'>${[logoHtml, ratingHtml, starsHtml].filter(Boolean).join('')}</span>`
        : ''

    const secondaryClusterHtml = countHtml
        ? `<span style='display:inline-flex;align-items:center;line-height:1;white-space:normal;min-width:0;max-width:100%;overflow-wrap:anywhere;flex:0 1 auto;'>${countHtml}</span>`
        : ''

    const clustersHtml = [primaryClusterHtml, secondaryClusterHtml].filter(Boolean).join('')
    const innerHtml = clustersHtml
        ? `<span style='display:flex;align-items:center;column-gap:0.35em;row-gap:0.14em;flex-wrap:wrap;max-width:100%;line-height:1;'>${clustersHtml}</span>`
        : ''

    const linkHref = (() => {
        if (linkTarget === 'custom') return customHref
        if (linkTarget === 'google' && mapsUrl && actionState.isGoogleEligible) return mapsUrl
        return ''
    })()

    if (linkHref) {
        const safeUrl = escapeAttr(linkHref)
        const linkAttrs = linkTarget === 'google' ? ` target='_blank' rel='noreferrer'` : ''
        const html = `<a href='${safeUrl}'${linkAttrs} style='${wrapperStyle}text-decoration:none;'>${innerHtml}</a>`
        return jsonEscape(html)
    }

    const html = `<span style='${wrapperStyle}'>${innerHtml}</span>`
    return jsonEscape(html)
}
